import {
  getWebhooksMetadata,
  getWebhooks,
  getWebhook,
  createWebhook,
  updateWebhook,
  deleteWebhook,
  getWebhookLogs,
  getAllWebhookLogs,
  getWebhookLog
} from "@/api/webhooks-api.js";

export default {
  namespaced: true,
  state: {
    webhooks: [],
    metadata: null,
    logs: [],
    selectedLog: null,
    loading: false,
    logsLoading: false,
    error: null
  },
  getters: {
    webhookTypes: state => {
      return state.metadata?.webhook_types || [];
    },
    entities: state => {
      return state.metadata?.entities || [];
    },
    operators: state => {
      return state.metadata?.operators || [];
    },
    logicOperators: state => {
      return state.metadata?.logic_operators || [];
    },
    httpMethods: state => {
      return state.metadata?.http_methods || [];
    },
    getWebhookById: state => id => {
      return state.webhooks.find(webhook => webhook._id === id);
    },
    getWebhookTypeConfig: state => type => {
      return state.metadata?.webhook_types?.find(wt => wt.type === type);
    },
    getEntityConfig: state => entityType => {
      return state.metadata?.entities?.find(e => e.entity_type === entityType);
    }
  },
  mutations: {
    setLoading(state, loading) {
      state.loading = loading;
    },
    setLogsLoading(state, loading) {
      state.logsLoading = loading;
    },
    setError(state, error) {
      state.error = error;
    },
    setMetadata(state, metadata) {
      state.metadata = metadata;
    },
    setWebhooks(state, webhooks) {
      state.webhooks = webhooks;
    },
    addWebhook(state, webhook) {
      state.webhooks.push(webhook);
    },
    updateWebhookInList(state, updatedWebhook) {
      const index = state.webhooks.findIndex(w => w._id === updatedWebhook._id);
      if (index !== -1) {
        state.webhooks.splice(index, 1, updatedWebhook);
      }
    },
    removeWebhook(state, webhookId) {
      state.webhooks = state.webhooks.filter(w => w._id !== webhookId);
    },
    setLogs(state, logs) {
      state.logs = logs;
    },
    setSelectedLog(state, log) {
      state.selectedLog = log;
    }
  },
  actions: {
    async fetchMetadata({ commit }, { realEstateGroupId }) {
      commit("setLoading", true);
      commit("setError", null);
      try {
        const response = await getWebhooksMetadata(realEstateGroupId);
        commit("setMetadata", response.data);
        return Promise.resolve(response.data);
      } catch (error) {
        commit("setError", error);
        return Promise.reject(error);
      } finally {
        commit("setLoading", false);
      }
    },
    async fetchWebhooks({ commit }) {
      commit("setLoading", true);
      commit("setError", null);
      try {
        const response = await getWebhooks();
        commit("setWebhooks", response.data || []);
        return Promise.resolve(response.data);
      } catch (error) {
        commit("setError", error);
        return Promise.reject(error);
      } finally {
        commit("setLoading", false);
      }
    },
    async fetchWebhook({ commit }, id) {
      commit("setLoading", true);
      commit("setError", null);
      try {
        const response = await getWebhook(id);
        return Promise.resolve(response.data);
      } catch (error) {
        commit("setError", error);
        return Promise.reject(error);
      } finally {
        commit("setLoading", false);
      }
    },
    async createWebhook({ commit }, webhookData) {
      commit("setLoading", true);
      commit("setError", null);
      try {
        const response = await createWebhook(webhookData);
        commit("addWebhook", response.data);
        return Promise.resolve(response.data);
      } catch (error) {
        commit("setError", error);
        return Promise.reject(error);
      } finally {
        commit("setLoading", false);
      }
    },
    async updateWebhook({ commit }, { id, webhookData }) {
      commit("setLoading", true);
      commit("setError", null);
      try {
        const response = await updateWebhook(id, webhookData);
        commit("updateWebhookInList", response.data);
        return Promise.resolve(response.data);
      } catch (error) {
        commit("setError", error);
        return Promise.reject(error);
      } finally {
        commit("setLoading", false);
      }
    },
    async deleteWebhook({ commit }, id) {
      commit("setLoading", true);
      commit("setError", null);
      try {
        await deleteWebhook(id);
        commit("removeWebhook", id);
        return Promise.resolve();
      } catch (error) {
        commit("setError", error);
        return Promise.reject(error);
      } finally {
        commit("setLoading", false);
      }
    },
    // ============= LOGS ACTIONS =============
    async fetchWebhookLogs({ commit }, { webhookId, params = {} }) {
      commit("setLogsLoading", true);
      commit("setError", null);
      try {
        const response = await getWebhookLogs(webhookId, params);
        commit("setLogs", response.data || []);
        return Promise.resolve(response.data);
      } catch (error) {
        commit("setError", error);
        return Promise.reject(error);
      } finally {
        commit("setLogsLoading", false);
      }
    },
    async fetchAllLogs({ commit }, params = {}) {
      commit("setLogsLoading", true);
      commit("setError", null);
      try {
        const response = await getAllWebhookLogs(params);
        commit("setLogs", response.data || []);
        return Promise.resolve(response.data);
      } catch (error) {
        commit("setError", error);
        return Promise.reject(error);
      } finally {
        commit("setLogsLoading", false);
      }
    },
    async fetchLogDetail({ commit }, logId) {
      commit("setLoading", true);
      commit("setError", null);
      try {
        const response = await getWebhookLog(logId);
        commit("setSelectedLog", response.data);
        return Promise.resolve(response.data);
      } catch (error) {
        commit("setError", error);
        return Promise.reject(error);
      } finally {
        commit("setLoading", false);
      }
    }
  }
};
